import type { ReactNode } from 'react';
import { DetalleContext, type Entidad } from './contexto-detalle';
import { abrirPestana } from '@/store/pestanas.store';
import { cn } from '@/lib/cn';

/**
 * EL DETALLE DE UNA COSA — se abre como PESTAÑA arriba, tipo navegador.
 *
 * Maciel: *«que una cosa me lleve a la otra»*. Abre un producto, desde ahí una
 * venta donde aparece, desde ahí el cliente. Cada una queda en su pestaña y la
 * tabla sigue en la suya, como se dejó: con su filtro y en su fila.
 *
 * ═══ POR QUÉ YA NO ES UN PANEL LATERAL ═══
 *
 * Lo era: un cajón a la derecha, apilado, con el fondo oscurecido. Regla del
 * dueño para todo Conversia (9-oct-2026): lo que se despliega para verlo o
 * trabajarlo va como pestaña, sin tapar ni desenfocar la pantalla, y con varias
 * abiertas a la vez. La pila se volvió la barra de pestañas
 * (`components/pestanas`), que además recuerda lo abierto al recargar.
 */

const ABRIDOR = {
  abrir: (e: Entidad) => abrirPestana({ tipo: e.tipo, id: e.id, titulo: e.etiqueta }),
};

/**
 * Envuelve una tabla (o una ficha) y le da a todo su árbol con qué abrir:
 *
 *     <ConDetalle>
 *       <DataTable … />
 *     </ConDetalle>
 *
 * Fuera de un `ConDetalle` los chips degradan a texto plano (ver `useAbrirDetalle`).
 */
export function ConDetalle({ children }: { children: ReactNode }) {
  return <DetalleContext.Provider value={ABRIDOR}>{children}</DetalleContext.Provider>;
}

/** Una sección del panel. Para que todos los detalles se vean igual. */
export function SeccionDetalle({
  titulo,
  accion,
  children,
}: {
  titulo: string;
  accion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{titulo}</h3>
        {accion}
      </div>
      {children}
    </section>
  );
}

/** Par etiqueta/valor. Los valores numéricos van tabulares y a la derecha. */
export function DatoDetalle({
  etiqueta,
  children,
  numerico,
}: {
  etiqueta: string;
  children: ReactNode;
  numerico?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <span className="shrink-0 text-slate-500">{etiqueta}</span>
      <span
        className={cn(
          'min-w-0 text-right text-slate-900 dark:text-slate-100',
          numerico && 'tabular-nums',
        )}
      >
        {children}
      </span>
    </div>
  );
}
